import { capabilitiesOf, EVENT_STATUSES, isEventStatus, type EventStatus } from './event-status.js';

/**
 * Welche Aktion ist in welchem Basar-Status möglich? (Plan 16, Abschnitt 4.12.3, U19a)
 * Reine Anzeigeregel nach dem Muster von `areaGate` und `settingLock`: Der Server prüft weiter selbst.
 * Die Verkäufer-Aktionen leiten sich aus `capabilitiesOf(status).sellersMayEditItems` ab, derselben Regel,
 * die `itemService` nutzt. Die Team-Aktionen (U19c) übernehmen genau die früheren lokalen Listen der API-Dienste
 * (Tabellentest in `event-actions.test.ts`). Die API nutzt dieselbe Regel für Prüfung und Text, das Web zeigt sie vorher an.
 */
export type EventAction =
  | 'item.create'
  | 'item.edit'
  | 'item.delete'
  | 'item.import'
  | 'item.carryover'
  | 'item.external_code'
  | 'item.discount'
  | 'label.print'
  | TeamAction;

/** Aktionen des Teams, die an den Basar-Status gebunden sind (Plan 16, Abschnitt 4.12.3, U19c). */
export type TeamAction =
  | 'seller.decide'
  | 'seller.add'
  | 'seller.invite'
  | 'helper.manage'
  | 'helper.join'
  | 'shift.signup'
  | 'station.manage'
  | 'register.pair'
  | 'timeslot.edit'
  | 'timeslot.book'
  | 'promo.create'
  | 'promo.cancel'
  | 'cash.count'
  | 'sale.record'
  | 'register.open';

export type ActionGate = { allowed: true } | { allowed: false; reason: string };

const ALLOWED: ActionGate = { allowed: true };

/** Grund, wenn Verkäufer Artikel nicht mehr bearbeiten dürfen (kurz, freundlich, nach Glossar). */
function itemLockReason(status: EventStatus): string {
  switch (status) {
    case 'selling':
      return 'Der Basar läuft – Artikel lassen sich nicht mehr anlegen, ändern oder löschen.';
    case 'returns':
      return 'Der Verkauf ist beendet – Artikel lassen sich nicht mehr anlegen, ändern oder löschen.';
    default:
      return 'Der Basar ist beendet – Artikel lassen sich nicht mehr anlegen, ändern oder löschen.';
  }
}

/** Beendete Basare (ab „Geschlossen“): Anmeldungen, Helfer und Buchungen sind vorbei, Zahlen endgültig. */
export const ENDED_EVENT_STATUSES: readonly EventStatus[] = ['closed', 'settled', 'archived'];
const ENDED = ENDED_EVENT_STATUSES;

interface TeamRule {
  /** Status, in denen die Aktion möglich ist. */
  statuses: readonly EventStatus[];
  reason: (status: EventStatus) => string;
}

const ALL_STATUSES: readonly EventStatus[] = EVENT_STATUSES;
const NOT_ENDED = ALL_STATUSES.filter((s) => !ENDED.includes(s));
const JOINABLE: readonly EventStatus[] = [
  'published',
  'registration_open',
  'intake',
  'selling',
  'returns',
];

const TEAM_RULES: Readonly<Record<TeamAction, TeamRule>> = {
  'seller.decide': {
    statuses: NOT_ENDED,
    reason: () => 'Der Basar ist beendet, Anmeldungen lassen sich nicht mehr entscheiden.',
  },
  'seller.add': {
    statuses: NOT_ENDED,
    reason: () => 'Der Basar ist beendet, es lässt sich niemand mehr hinzufügen.',
  },
  'seller.invite': {
    statuses: ['published', 'registration_open'],
    reason: () =>
      'Einladen lässt sich erst, wenn der Basar veröffentlicht ist, und nur bis zum Ende der Anmeldung.',
  },
  // Einladungslinks lassen sich schon im Entwurf vorbereiten, beitreten gilt erst ab „Veröffentlicht“
  'helper.manage': {
    statuses: ['draft', ...JOINABLE],
    reason: () => 'Der Basar ist beendet, es gibt keine neuen Helfer mehr.',
  },
  'helper.join': {
    statuses: JOINABLE,
    reason: (status) =>
      status === 'draft'
        ? 'Der Basar ist noch nicht veröffentlicht, Helfer können noch nicht beitreten.'
        : 'Der Basar ist beendet, es gibt keine neuen Helfer mehr.',
  },
  'shift.signup': {
    statuses: JOINABLE,
    reason: (status) =>
      status === 'draft'
        ? 'Der Basar ist noch nicht veröffentlicht, Schichten lassen sich noch nicht buchen.'
        : 'Der Basar ist beendet, Schichten lassen sich nicht mehr buchen.',
  },
  'station.manage': {
    statuses: NOT_ENDED,
    reason: () => 'Der Basar ist beendet, Kassenplätze lassen sich nicht mehr ändern.',
  },
  'register.pair': {
    statuses: ['intake', 'selling', 'returns'],
    reason: () =>
      'Für diesen Basar lässt sich im Moment keine Kasse koppeln (nur während Annahme, Verkauf und Rückgabe).',
  },
  'timeslot.edit': {
    statuses: ALL_STATUSES.filter((s) => s !== 'archived'),
    reason: () => 'Der Basar ist archiviert, Zeitfenster lassen sich nicht mehr ändern.',
  },
  'timeslot.book': {
    statuses: NOT_ENDED,
    reason: () => 'Der Basar ist beendet, Zeitfenster sind nicht mehr buchbar.',
  },
  'promo.create': {
    statuses: ['intake', 'selling'],
    reason: () => 'Rabattaktionen lassen sich nur während der Annahme und des Verkaufs anlegen.',
  },
  'promo.cancel': {
    statuses: ['intake', 'selling', 'returns', 'closed'],
    reason: () => 'In diesem Status lässt sich keine Aktion mehr absagen.',
  },
  // Kassiertes Geld darf nie an einem Statuswechsel scheitern: Nach „Geschlossen“ und der Abrechnung nehmen Kassen
  // Verkäufe nur noch als Upload der Offline-Warteschlange an (salesService, U19f)
  'sale.record': {
    statuses: ['selling', 'returns', 'closed', 'settled'],
    reason: (status) =>
      status === 'archived'
        ? 'Der Basar ist archiviert, es werden keine Verkäufe mehr angenommen.'
        : 'Verkäufe werden erst ab dem Verkaufsstart angenommen.',
  },
  // Kassen lassen sich schon am Vorabend (Annahme) eröffnen
  'register.open': {
    statuses: ['intake', 'selling', 'returns'],
    reason: () => 'Eine Kasse lässt sich nur während Annahme, Verkauf und Rückgabe eröffnen.',
  },
  'cash.count': {
    statuses: ['intake', 'selling', 'returns', 'closed', 'settled'],
    reason: (status) =>
      status === 'archived'
        ? 'Der Basar ist archiviert, der Bargeldabgleich ist nicht mehr möglich.'
        : 'Der Bargeldabgleich ist erst ab der Warenannahme möglich.',
  },
};

export function eventAction(action: EventAction, status: EventStatus): ActionGate {
  if (action === 'label.print') return ALLOWED;
  if (action in TEAM_RULES) {
    const rule = TEAM_RULES[action as TeamAction];
    return rule.statuses.includes(status)
      ? ALLOWED
      : { allowed: false, reason: rule.reason(status) };
  }
  if (capabilitiesOf(status).sellersMayEditItems) return ALLOWED;
  return { allowed: false, reason: itemLockReason(status) };
}

/**
 * Wie `eventAction`, aber für Status als Zeichenkette (Datenbankzeile, SQL-Prüfung). Ein unbekannter Status
 * erlaubt nichts. Der Grund ist dann allgemein gehalten.
 */
export function eventActionOf(action: EventAction, status: string): ActionGate {
  if (!isEventStatus(status))
    return { allowed: false, reason: 'Der Basar ist in diesem Zustand nicht bearbeitbar.' };
  return eventAction(action, status);
}

/** Alle Status, in denen die Aktion möglich ist (für `status IN (?)` in SQL und Tabellentests). */
export function statusesAllowing(action: EventAction): readonly EventStatus[] {
  return ALL_STATUSES.filter((status) => eventAction(action, status).allowed);
}

function teamNotice(status: EventStatus): string | null {
  switch (status) {
    case 'draft':
    case 'published':
    case 'registration_open':
    case 'intake':
    case 'selling':
      return null;
    case 'returns':
      return 'Der Verkauf ist beendet, jetzt läuft die Rückgabe.';
    case 'closed':
      return 'Der Basar ist geschlossen, jetzt folgt die Abrechnung.';
    case 'settled':
    case 'archived':
      return 'Der Basar ist abgerechnet und nur noch zum Ansehen da.';
  }
}

/** Ein Satz zum Zustand für den Kopf einer Seite, je Zielgruppe; `null` = nichts anzeigen. */
export function statusNotice(status: EventStatus, audience: 'seller' | 'team'): string | null {
  if (audience === 'team') return teamNotice(status);
  switch (status) {
    case 'draft':
    case 'published':
    case 'registration_open':
      return null;
    case 'intake':
      return 'Die Warenannahme läuft. Neue Artikel können Sie noch bis zum Verkaufsstart anlegen, abgegebene Artikel ändert nur die Annahme.';
    case 'selling':
      return 'Der Basar läuft – Artikel können nicht mehr angelegt, geändert oder gelöscht werden. Etiketten drucken geht weiter.';
    case 'returns':
      return 'Der Verkauf ist beendet, jetzt läuft die Rückgabe. Ihren Stand sehen Sie unter „Aktueller Stand Verkauf“.';
    case 'closed':
      return 'Der Basar ist geschlossen. Die Abrechnung folgt.';
    case 'settled':
    case 'archived':
      return 'Der Basar ist abgerechnet. Ihre Abrechnung finden Sie unter „Meine Berichte“.';
  }
}
